import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Building2, Check, ChevronsUpDown, Search, X } from 'lucide-react';
import { useUserSettingsContext } from '../../../context/UserSettingsContext';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../ui/dropdown-menu';
import { SidebarMenu, SidebarMenuButton, SidebarMenuItem, useSidebar } from '../../ui/sidebar';
import brandMark from '../../../assets/img/Icon-Transparent.png';
import { reloadForOrgSwitch } from '../../../utils/orgSwitchPath';

const byName = (a, b) => (a?.name || '').localeCompare(b?.name || '', undefined, { sensitivity: 'base' });

/**
 * Sidebar header: brand mark + active organization. Opens a searchable org
 * list when the user belongs to more than one (ported from the old sidebar
 * footer, including Enter-to-pick on a single match).
 */
export default function OrgSwitcher({ variant = 'sidebar' }) {
  const appBar = variant === 'appbar';
  const { isMobile } = useSidebar();
  const location = useLocation();
  const { currentOrganization, organizations, setCurrentOrg } = useUserSettingsContext();
  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState('');
  const inputRef = useRef(null);

  const orgs = useMemo(() => {
    const list = Array.isArray(organizations) ? [...organizations] : [];
    const needle = term.trim().toLowerCase();
    return (needle ? list.filter((o) => (o.name || '').toLowerCase().includes(needle)) : list).sort(byName);
  }, [organizations, term]);

  const canSwitch = Array.isArray(organizations) && organizations.length > 1;

  useEffect(() => {
    if (open) {
      const id = setTimeout(() => inputRef.current?.focus(), 50);
      return () => clearTimeout(id);
    }
    setTerm('');
    return undefined;
  }, [open]);

  const pick = (org) => {
    if (!setCurrentOrg) {
      console.error('setCurrentOrg function not available from context.');
      return;
    }
    try {
      setCurrentOrg(org.code);
      setOpen(false);
      // Stay on the current page, reloaded clean for the new organization.
      reloadForOrgSwitch(location.pathname);
    } catch (error) {
      console.error('Error changing organization:', error);
    }
  };

  // Phone app bar: the organization is the headline (the bar already reads
  // as Lodgeic), the chevron sits right after the name.
  const header = appBar ? (
    <>
      <div className="flex aspect-square size-7 shrink-0 items-center justify-center rounded-md bg-[var(--sidebar-surface-2)] ring-1 ring-[var(--sidebar-border)]">
        <img src={brandMark} alt="" className="size-[18px] object-contain" />
      </div>
      <span className="min-w-0 truncate text-[15px] font-semibold tracking-[-0.01em] text-[var(--sidebar-strong)]">
        {currentOrganization?.name || 'Lodgeic'}
      </span>
      {canSwitch && <ChevronsUpDown className="size-3.5 shrink-0 text-sidebar-muted" />}
    </>
  ) : (
    <>
      <div className="flex aspect-square size-8 shrink-0 items-center justify-center rounded-lg bg-[var(--sidebar-surface-2)] ring-1 ring-[var(--sidebar-border)]">
        <img src={brandMark} alt="" className="size-5 object-contain" />
      </div>
      <div className="grid flex-1 text-left leading-tight">
        <span className="truncate text-sm font-semibold text-[var(--sidebar-strong)]">Lodgeic</span>
        <span className="truncate text-xs text-sidebar-muted">{currentOrganization?.name || 'Select organization'}</span>
      </div>
      {canSwitch && <ChevronsUpDown className="ml-auto size-4 text-sidebar-muted" />}
    </>
  );
  const buttonSize = appBar ? 'default' : 'lg';
  const buttonClass = appBar ? 'h-10 w-auto max-w-full gap-2 px-1.5' : '';

  if (!canSwitch) {
    return (
      <SidebarMenu>
        <SidebarMenuItem>
          <SidebarMenuButton size={buttonSize} className={`cursor-default hover:bg-transparent active:bg-transparent ${buttonClass}`}>
            {header}
          </SidebarMenuButton>
        </SidebarMenuItem>
      </SidebarMenu>
    );
  }

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu open={open} onOpenChange={setOpen}>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton size={buttonSize} className={`data-[state=open]:bg-sidebar-accent ${buttonClass}`} aria-label="Switch organization">
              {header}
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className={appBar ? 'w-[min(20rem,calc(100vw-24px))] p-0' : 'w-[--radix-dropdown-menu-trigger-width] min-w-72 p-0'}
            align="start"
            side={isMobile ? 'bottom' : 'right'}
            sideOffset={8}
          >
            <DropdownMenuLabel className="px-3 pt-3 text-[11px] uppercase tracking-wider">Organizations</DropdownMenuLabel>
            <div className="relative px-2 pb-2">
              <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-[60%] text-[var(--lodge-onyx-400)]" />
              <input
                ref={inputRef}
                type="text"
                value={term}
                onChange={(e) => setTerm(e.target.value)}
                // Radix menus hijack typeahead/arrow keys — keep typing in the box.
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && term.trim() && orgs.length === 1) {
                    e.preventDefault();
                    pick(orgs[0]);
                    return;
                  }
                  if (e.key !== 'Escape' && e.key !== 'ArrowDown') e.stopPropagation();
                }}
                placeholder="Search organizations..."
                className="app-shell-plain-input h-9 w-full rounded-md border border-[var(--lodge-onyx-100)] bg-transparent pl-8 pr-8 text-sm text-[var(--lodge-onyx-900)] placeholder:text-[var(--lodge-onyx-400)]"
              />
              {term && (
                <button
                  type="button"
                  aria-label="Clear search"
                  onClick={() => { setTerm(''); inputRef.current?.focus(); }}
                  className="absolute right-4 top-1/2 -translate-y-[60%] text-[var(--lodge-onyx-400)] hover:text-[var(--lodge-onyx-900)]"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
            <DropdownMenuSeparator className="mx-0 my-0" />
            <div className="max-h-72 overflow-y-auto p-1">
              {orgs.length === 0 ? (
                <div className="p-3 text-center text-sm text-[var(--lodge-onyx-400)]">No organizations found</div>
              ) : (
                orgs.map((org) => {
                  const active = currentOrganization?.code === org.code;
                  return (
                    <DropdownMenuItem key={org.code} onSelect={() => pick(org)} className="gap-2">
                      <Building2 className={active ? 'text-[var(--lodge-orange-400)]' : 'text-[var(--lodge-onyx-400)]'} />
                      <span className={active ? 'font-medium' : undefined}>{org.name}</span>
                      {active && <Check className="ml-auto text-[var(--lodge-orange-400)]" />}
                    </DropdownMenuItem>
                  );
                })
              )}
            </div>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}
